import './styles.scss';
import { useFirestore } from 'reactfire';
import { collection, query, orderBy, limit, where, deleteDoc, doc, startAfter, getDocs } from 'firebase/firestore';
import { useEffect, useState, useMemo } from 'react';
import CMSTable from '../CMSTable';
import useParksById from '../../hooks/useParksById';
import { Link, useSearchParams } from 'react-router-dom';
import { range } from 'd3-array';
import confirm from '../Dialog';
import { useDebounce, useLocalStorage } from 'react-use';
import Publish from './Publish';
import usePublishedData from '../../hooks/usePublishedData';
import { chunk } from 'lodash';
import { PulseLoader } from 'react-spinners';
import pinIcon from '../../images/cmsMarker.png'
import hasImagesIcon from '../../images/icon-has-images.svg'
import hasBackgroundIcon from '../../images/icon-has-background.svg'

const minYear = 1921
const maxYear = new Date().getFullYear()

export default function CMSGroves(props) {
  const [numResultsToShow, setNumResultsToShow] = useState(100);
  const [startAtRefs, setStartAtRefs] = useState([''])
  // const [previousStartAtRefs, setPreviousStartAtRefs] = useState([''])

  const firestore = useFirestore();
  const parksById = useParksById()
  const [search, setSearch] = useLocalStorage('grovesIndexSearch', '')
  const [searchTextInput, setSearchTextInput] = useState(search || '' )
  const [publishCount, setPublishCount] = useState(0)
  const publishedData = usePublishedData(publishCount)
  const [fromYear, setFromYear] = useLocalStorage('grovesIndexMinYear', minYear)
  const [toYear, setToYear] = useLocalStorage('grovesIndexMaxYear', maxYear)
  const grovesCollection = useMemo(() => collection(firestore, 'groves'), [firestore])
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useLocalStorage('unpublishedChanges', false)
  const [sortColumn, setSortColumn] = useLocalStorage('grovesIndexSortColumn', 'name')
  const [sortDirection, setSortDirection] = useLocalStorage('grovesIndexSortDirection', 'asc')
  const [deleteCount, setDeleteCount] = useState(0)
   const sort = (column) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
    setStartAtRefs([])
  }
  const grovesQuery = useMemo(() => {
    const args = [
      grovesCollection,
      +fromYear !== +minYear ? where('yearDedicated', '>=', fromYear) : null,
      +toYear !== +maxYear ? where('yearDedicated', '<=', toYear): null,
      sortColumn !== 'yearDedicated' && (+fromYear !== +minYear || +toYear !== +maxYear) ? orderBy('yearDedicated', 'asc') : null,
      orderBy(sortColumn, sortDirection),
      limit(numResultsToShow),
      startAtRefs[startAtRefs.length - 1] ? (sortDirection === 'asc' ? startAfter : startAfter)(startAtRefs[startAtRefs.length - 1]) : null
  ].filter(d => !!d)
    return query.apply(this, args)
  }, [numResultsToShow, fromYear, toYear, sortColumn, sortDirection, startAtRefs, grovesCollection]);
  // let { data: groves, ...rest } = useFirestoreCollectionData(grovesQuery);
  const [datastoreGroves, setDatastoreGroves] = useState([])
  const [groveDocs, setGroveDocs] = useState([])
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    const fetch = async() => {
      setLoading(true)
      const snapshot = await getDocs(grovesQuery)
      const groves = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        NO_ID_FIELD: doc.id,
      }))
      setGroveDocs(snapshot.docs)
      setDatastoreGroves(groves)
      setLoading(false)
    }
    if (search) {
      return
    }
    fetch()
  }, [grovesQuery, search, deleteCount])
  let groves = datastoreGroves
  let filteredGroveCount = 0
  if (publishedData && (search || (+fromYear !== +minYear) || (+toYear !== +maxYear))) {
    const publishedParksById = publishedData.parks.reduce((acc, park) => {
      acc[park.id] = park
      return acc
    }, {})

    groves = [...publishedData.groves].filter(d => {
      if (!search) return true
      const parkName = publishedParksById[d.park] ? publishedParksById[d.park].name : ''
      return d.name.toLowerCase().includes(search.toLowerCase()) ||
        d.dedicatedBy.toLowerCase().includes(search.toLowerCase()) ||
        parkName.toLowerCase().includes(search.toLowerCase())


    })
    .filter(d => +fromYear === +minYear || d.yearDedicated >= fromYear)
    .filter(d => +toYear === +maxYear || d.yearDedicated <= toYear)

    groves.sort((a, b) => {
      return (a[sortColumn] > b[sortColumn] ? 1 : -1) * (sortDirection === 'asc' ? 1 : -1)

    })
    filteredGroveCount = groves.length
    const chunked = chunk(groves, numResultsToShow)
    groves = chunked[startAtRefs.length]

  }
  const [searchParams] = useSearchParams()
  const [deleted, setDeleted] = useState(false)
  const [rowToDelete, setRowToDelete] = useState(null)
  const searchParamStatusString = searchParams.get('status')
  const validStatuses = ['added', 'edited']
  const searchParamStatus = validStatuses.includes(searchParamStatusString) ? searchParamStatusString : null
  const formStatus = deleted ? 'deleted' : searchParamStatus
  const status = formStatus ? <div className={`status ${formStatus}`}>Grove {formStatus}</div> : null

  const deleteGrove = (row) => async (e) => {
    setDeleted(false)
    setRowToDelete(row)
    if (await confirm({
      confirmation: <>You are about to delete <strong>{row.name}</strong>. Are you sure you want to do this?</>,
      actionText: 'Delete',
      actionColor: '#D34427',
    })) {
      await deleteDoc(doc(grovesCollection, row['NO_ID_FIELD']))
      setDeleted(true)
      setHasUnpublishedChanges(true)
      setDeleteCount(deleteCount + 1)
    } else {
    }
    setRowToDelete(null)
  }
  const grovesTable = groves ?
    <CMSTable
      data={groves}
      sortable
      sort={sort}
      rowToDelete={rowToDelete}
      columns={[
        {
          name: 'Grove Name',
          accessor: row => row.name,
          key: 'name',
          width: '30%',
        },
        {
          name: 'Year',
          accessor: row => row.yearDedicated,
          key: 'yearDedicated',
          width: '50px'
        },
        {
          name: 'Donor Name',
          accessor: row => row.dedicatedBy,
          key: 'dedicatedBy',
          width: '40%'
        },
        {
          name: 'Park',
          accessor: row => parksById[row.park] ? parksById[row.park].name : '',
          key: 'park',
          width: '30%',
        },
        {
          name: '',
          accessor: row => {
            return row.images && row.images.length ? <img className='icon' src={hasImagesIcon} alt='has images' data-tip='Has images' /> : null
          },
          key: 'images',
          notSortable: true,
        },
        {
          name: '',
          accessor: row => {
            return row.backgroundStory ? <img className='icon'  src={hasBackgroundIcon} alt='has background story' data-tip='Has background story' /> : null
          },
          key: 'backgroundStory',
          notSortable: true,
        },

        {
          name: '',
          accessor: row => {
            return row.lat && row.lng ? (
              <img style={{ width: '1em'}} src={pinIcon} alt={`grove located at ${row.lat}, ${row.lng}`} data-tip={`${(+row.lat).toFixed(4)}, ${(+row.lng).toFixed(4)}`} />
            ) : null

          },
          key: 'latLng',
          notSortable: true,
        }
      ]}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      showEditControls
      deleteRow={deleteGrove}
    />
    : null

  const less = () => {
    if (startAtRefs.length > 0) {
      setStartAtRefs(startAtRefs.slice(0, -1))
    }
  }
  const more = () => {
    setStartAtRefs(refs => [...refs, groveDocs[groveDocs.length - 1]])
  }

  const updateSearchField = (e) => {
    setSearchTextInput(e.target.value)
  }
  const clearSearch = () => {
    setSearchTextInput('')
    setSearch('')
    setStartAtRefs([])
  }
  useDebounce(() => {
    if (search !== searchTextInput) {
      setSearch(searchTextInput)
    }
    if (startAtRefs.length) {
      setStartAtRefs([])
    }
  }, 300, [searchTextInput])

  const updatePublishCount = (v) => {
    setHasUnpublishedChanges(false)
    setPublishCount(v)
  }

  const publishedStatus = hasUnpublishedChanges ? <Publish setPublishCount={updatePublishCount} /> : publishCount > 0 ?
    <div className='status'>Groves published</div> : null

  const showStartCount = numResultsToShow * (startAtRefs.length) + 1
  const showEndCount = showStartCount + (groves ? groves.length : 0) - 1
  const totalCount = publishedData && publishedData.groves ? (search ? filteredGroveCount : publishedData.groves.length) : ''
  return (
    <div className='CMSGroves'>
      <h2>Groves</h2>
      {status}
      {publishedStatus}
      <div className='topControls'>
        <div className='searchInput'>
          <input size={40} type="text" placeholder="Search by Grove, Donor or Park" className='search' value={searchTextInput} onChange={updateSearchField} />
          {searchTextInput ? <div className='clearSearch' onClick={clearSearch}>&times;</div> : null}
        </div>
        <div className='years'>
          <YearDropdown year={fromYear} setYear={setFromYear} label='From' />
          <YearDropdown year={toYear} setYear={setToYear} label='To' />
        </div>
        <div className='addButtonWrapper'>
          <Link to='add' className='add button'><span className='cancelX'>+</span> Add Grove</Link>
        </div>
      </div>
      {
        search && hasUnpublishedChanges ? (
          <div className='status'>
            Text searches only search published data, please publish your changes if you don't see an expected grove listed
          </div>
        ) : null
      }
      <div className='tableWrapper'>
        {grovesTable}
        {loading ?
          <div className='loading'>
            <PulseLoader size={'1em'} color="#14816E" />
          </div>
          : null
        }
      </div>
      <div className='bottomControls'>
        <div style={{ width: '200px'}}/>
        <div>
          <button onClick={less} className='prev' style={{
            visibility: startAtRefs.length > 0 ? 'visible' : 'hidden'
          }}>Prev</button>
          <span>
            Showing {showStartCount} - {showEndCount} of {totalCount}
          </span>
          <button onClick={more} className='next'>Next</button>
        </div>
        <div>
          Number of Results:{' '}
          <select value={numResultsToShow} onChange={e => setNumResultsToShow(e.target.value)}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
    </div>
  )
}

function YearDropdown(props) {
  const { year, setYear, label } = props
  const years = range(minYear, maxYear + 1)
  return (
    <div className='yearDropdown'>
      <label>{label}</label>
      <select value={year} onChange={e => setYear(e.target.value)}>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  )
}
